import config from '../config';
import { msalInstance } from '../App';
import { loginRequest } from '../authConfig';

class ApiService {
  /**
   * Get default headers including Authorization Bearer token and user email.
   * Async because token acquisition via MSAL is async.
   */
  static async getDefaultHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const account = accounts[0];
        headers['User-Email'] = account.username;

        // Acquire token silently — MSAL returns cached token or renews it
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account,
        });
        headers['Authorization'] = `Bearer ${tokenResponse.accessToken}`;
      }
    } catch (error) {
      console.warn('Could not acquire auth token for API request:', error);
    }

    return headers;
  }

  static async get(endpoint) {
    const response = await fetch(`${config.apiUrl}${endpoint}`, {
      headers: await this.getDefaultHeaders()
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    return await response.json();
  }

  static async post(endpoint, data) {
    const response = await fetch(`${config.apiUrl}${endpoint}`, {
      method: 'POST',
      headers: await this.getDefaultHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      let detail = '';
      try { const b = await response.json(); detail = b.error || JSON.stringify(b); } catch {}
      throw new Error(`API Error: ${response.status}${detail ? ' - ' + detail : ''}`);
    }
    return await response.json();
  }

  static async put(endpoint, data) {
    const response = await fetch(`${config.apiUrl}${endpoint}`, {
      method: 'PUT',
      headers: await this.getDefaultHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    return await response.json();
  }

  static async delete(endpoint) {
    const response = await fetch(`${config.apiUrl}${endpoint}`, {
      method: 'DELETE',
      headers: await this.getDefaultHeaders(),
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    return await response.json();
  }

  static getEndpointForRequest(requestId, source, path = '') {
    let endpoint;
    let actualRequestId = requestId;

    // If source is provided, use it directly
    if (source) {
      actualRequestId = typeof requestId === 'string' && requestId.includes('_')
        ? requestId.split('_').pop()
        : requestId;

      const sourceMap = {
        'agn_firewall_requests': `/api/agn-firewall/${actualRequestId}${path}`,
        'webex_decommissioning_requests': `/api/webex-decommissioning/${actualRequestId}${path}`,
        'ipt_decommissioning_requests': `/api/ipt-decommissioning/${actualRequestId}${path}`,
        'circuit_order_requests': `/api/circuit-orders/${actualRequestId}${path}`,
        'requests': `/api/requests/${actualRequestId}${path}`
      };

      return sourceMap[source] || `/api/requests/${actualRequestId}${path}`;
    }

    // Otherwise, detect from request ID prefix
    const requestIdStr = requestId.toString();

    if (requestIdStr.startsWith('Firewall_Decom_')) {
      actualRequestId = requestIdStr.replace('Firewall_Decom_', '');
      endpoint = `/api/agn-firewall/${actualRequestId}${path}`;
    } else if (requestIdStr.startsWith('Webex_Decom_')) {
      actualRequestId = requestIdStr.replace('Webex_Decom_', '');
      endpoint = `/api/webex-decommissioning/${actualRequestId}${path}`;
    } else if (requestIdStr.startsWith('IPT_Decom_')) {
      actualRequestId = requestIdStr.replace('IPT_Decom_', '');
      endpoint = `/api/ipt-decommissioning/${actualRequestId}${path}`;
    } else if (requestIdStr.startsWith('Bandwith_Upgrade_')) {
      actualRequestId = requestIdStr.replace('Bandwith_Upgrade_', '');
      endpoint = `/api/requests/${actualRequestId}${path}`;
    } else {
      endpoint = `/api/requests/${requestId}${path}`;
    }

    return endpoint;
  }

  static async addFulfillmentUpdate(requestId, comment) {
    try {
      const endpoint = this.getEndpointForRequest(requestId, null, '/fulfillment-update');

      console.log(`🔍 Adding fulfillment update to endpoint: ${endpoint}`);

      const response = await fetch(`${config.apiUrl}${endpoint}`, {
        method: 'POST',
        headers: await this.getDefaultHeaders(),
        body: JSON.stringify({ comment }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding fulfillment update:', error);
      throw error;
    }
  }

  // Add this new method for request submission with Amelia integration
  static async submitRequest(data) {
    try {
      const response = await fetch(`${config.apiUrl}/api/requests/`, {
        method: 'POST',
        headers: await this.getDefaultHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting request:', error);
      throw error;
    }
  }

  // Add this new method to the ApiService class
  static async getExcelData(columnName) {
    try {
      const response = await fetch(`${config.apiUrl}/api/requests/excel-lookup/${encodeURIComponent(columnName)}`, {
        headers: await this.getDefaultHeaders()
      });
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching Excel data for ${columnName}:`, error);
      throw error;
    }
  }

  // Add this new method to the ApiService class
  static async getSiteDetails(siteId) {
    try {
      const response = await fetch(`${config.apiUrl}/api/requests/site-details/${encodeURIComponent(siteId)}`, {
        headers: await this.getDefaultHeaders()
      });
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching site details for ${siteId}:`, error);
      throw error;
    }
  }

  // Add a new method to ApiService
  static async calculateCircuitCosts(circuits, country, siteType = "Normal Site") {
    try {
      const response = await fetch(`${config.apiUrl}/api/requests/calculate-circuit-costs`, {
        method: 'POST',
        headers: await this.getDefaultHeaders(),
        body: JSON.stringify({ circuits, country, siteType }),
      });

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error calculating circuit costs:', error);
      throw error;
    }
  }
  // Add this new method to the ApiService class
    static async checkGrmNumber(grmNumber) {
      try {
        const response = await fetch(`${config.apiUrl}/api/generic/check-grm-number/${grmNumber}`, {
          headers: await this.getDefaultHeaders()
        });
        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }
        return await response.json();
      } catch (error) {
        console.error('Error checking GRM number:', error);
        throw error;
      }
    }

  // Add this method after your other API methods
  static async patch(endpoint, data) {
    const response = await fetch(`${config.apiUrl}${endpoint}`, {
      method: 'PATCH',
      headers: await this.getDefaultHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    return await response.json();
  }

  // Add these properties just after the class declaration
  static _requestsCache = null;
  static _agnRequestsCache = null;
  static _webexRequestsCache = null;
  static _iptRequestsCache = null;
  static _circuitRequestsCache = null;
  static _cacheTimestamp = null;
  static CACHE_DURATION = 30000; // 30 seconds cache duration

  // Add this method to get data from cache
  static getRequestsFromCache(userRole) {
    const currentTime = new Date().getTime();

    // Check if we have a valid cache
    if (this._requestsCache &&
        this._agnRequestsCache &&
        this._webexRequestsCache &&
        this._iptRequestsCache &&
        this._circuitRequestsCache &&
        this._cacheTimestamp &&
        (currentTime - this._cacheTimestamp < this.CACHE_DURATION)) {
      console.log('🔍 Using cached requests data');

      // Apply user filtering on cached data if needed
      let filteredAgnRequests = this._agnRequestsCache;
      let filteredWebexRequests = this._webexRequestsCache;
      let filteredIptRequests = this._iptRequestsCache;
      let filteredCircuitRequests = this._circuitRequestsCache;

      if (!userRole.isAdmin && userRole.userEmail) {
        filteredAgnRequests = this._agnRequestsCache.filter(req =>
          req.customer_contact_mail === userRole.userEmail);
        filteredWebexRequests = this._webexRequestsCache.filter(req =>
          req.customer_contact_mail === userRole.userEmail);
        filteredIptRequests = this._iptRequestsCache.filter(req =>
          req.customer_contact_mail === userRole.userEmail);
        filteredCircuitRequests = this._circuitRequestsCache.filter(req =>
          req.customer_contact_mail === userRole.userEmail);
      }

      // Process and return the data from cache
      const allRequests = [
        ...this._requestsCache.map(req => ({
          ...req,
          id: `Bandwith_Upgrade_${req.id}`,
          original_id: req.id,
          source: 'requests',
          request_type: req.request_type || 'Bandwidth Upgrade/Downgrade'
        })),
        ...filteredAgnRequests.map(req => ({
          ...req,
          id: `Firewall_Decom_${req.id}`,
          original_id: req.id,
          source: 'agn_firewall_requests',
          request_type: req.odm_type || 'AGN Managed-Firewall Decommissioning',
          odm_title: req.odm_title || req.odmTitle || 'N/A',
          request_state: req.request_state || req.requestState || 'N/A',
          request_status: req.request_status || req.requestStatus || 'N/A',
          approval_status: req.approval_status || req.approvalStatus || 'N/A'
        })),
        ...filteredWebexRequests.map(req => ({
          ...req,
          id: `Webex_Decom_${req.id}`,
          original_id: req.id,
          source: 'webex_decommissioning_requests',
          request_type: req.odm_type || 'AGN Webex Decommissioning',
          odm_title: req.odm_title || req.odmTitle || 'N/A',
          request_state: req.request_state || req.requestState || 'N/A',
          request_status: req.request_status || req.requestStatus || 'N/A',
          approval_status: req.approval_status || req.approvalStatus || 'N/A',
          excel_file_name: req.excel_file_name || req.excelFileName,
          excel_file_size: req.excel_file_size || req.excelFileSize,
          has_excel_attachment: !!(req.excel_file_name || req.excelFileName)
        })),
        ...filteredIptRequests.map(req => ({
          ...req,
          id: `IPT_Decom_${req.id}`,
          original_id: req.id,
          source: 'ipt_decommissioning_requests',
          request_type: req.odm_type || 'AGN IPT Decommissioning',
          odm_title: req.odm_title || req.odmTitle || 'N/A',
          request_state: req.request_state || req.requestState || 'N/A',
          request_status: req.request_status || req.requestStatus || 'N/A',
          approval_status: req.approval_status || req.approvalStatus || 'N/A',
          excel_file_name: req.excel_file_name || req.excelFileName,
          excel_file_size: req.excel_file_size || req.excelFileSize,
          has_excel_attachment: !!(req.excel_file_name || req.excelFileName)
        })),
        ...filteredCircuitRequests.map(req => ({
          ...req,
          id: `Circuit_Order_${req.id}`,
          original_id: req.id,
          source: 'circuit_order_requests',
          request_type: 'Circuit Ordering',
          odm_title: req.odm_title || 'N/A',
          request_state: req.request_state || 'Draft',
          request_status: req.request_status || 'New',
          approval_status: req.approval_status || 'Pending',
          acai_request_id: req.acai_request_id,
        }))
      ];

      return allRequests;
    }

    // Return null if cache is not valid
    return null;
  }

  // Add this method to update the cache
  static updateRequestsCache(bandwidthRequests, agnRequests, webexRequests, iptRequests, circuitRequests) {
    this._requestsCache = bandwidthRequests;
    this._agnRequestsCache = agnRequests;
    this._webexRequestsCache = webexRequests || [];
    this._iptRequestsCache = iptRequests || [];
    this._circuitRequestsCache = circuitRequests || [];
    this._cacheTimestamp = new Date().getTime();
    console.log('✅ Requests cache updated (Bandwidth, AGN Firewall, Webex, IPT, Circuit Orders)');
  }

  // Add this method to invalidate the cache
  static invalidateRequestsCache() {
    this._requestsCache = null;
    this._agnRequestsCache = null;
    this._webexRequestsCache = null;
    this._iptRequestsCache = null;
    this._circuitRequestsCache = null;
    this._cacheTimestamp = null;
    console.log('🗑️ Requests cache invalidated');
  }

  // Add these methods to your ApiService class:
  static async getWithRetry(endpoint, maxRetries = 3, timeout = 15000) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`🔄 API call attempt ${attempt}/${maxRetries}: ${endpoint}`);
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);
        
        const response = await fetch(`${config.apiUrl}${endpoint}`, {
          signal: controller.signal,
          headers: {
            ...await this.getDefaultHeaders(),
            'Cache-Control': 'no-cache'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }
        
        const data = await response.json();
        console.log(`✅ API call successful on attempt ${attempt}`);
        return data;
        
      } catch (error) {
        console.error(`❌ API call attempt ${attempt} failed:`, error.message);
        
        if (attempt === maxRetries) {
          throw error;
        }
        
        // Exponential backoff
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
        console.log(`⏱️ Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  // Replace your existing get method:
  static async get(endpoint, timeout = 15000) {
    return this.getWithRetry(endpoint, 3, timeout);
  }

  // Replace your existing post method:
  static async post(endpoint, data, timeout = 15000) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(`${config.apiUrl}${endpoint}`, {
        method: 'POST',
        headers: await this.getDefaultHeaders(),
        body: JSON.stringify(data),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timed out. Please try again.');
      }
      throw error;
    }
  }

  // Update checkGrmNumber with better timeout:
  static async checkGrmNumber(grmNumber) {
    try {
      console.log(`🔍 Checking GRM number: ${grmNumber}`);
      const response = await this.getWithRetry(`/api/generic/check-grm-number/${grmNumber}`, 3, 20000);
      console.log(`✅ GRM check completed for: ${grmNumber}`);
      return response;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.error('GRM check timed out after 20 seconds');
        throw new Error('Request timed out. Please try again.');
      }
      console.error('Error checking GRM number:', error);
      throw error;
    }
  }

  // Add this method for better request fetching with fallback:
  static async getRequestsWithFallback(userRole) {
    try {
      // First try cache
      const cachedRequests = this.getRequestsFromCache(userRole);
      if (cachedRequests) {
        console.log('📋 Using cached requests data');
        return cachedRequests;
      }
      
      console.log('📡 Fetching fresh requests data...');

      // Fetch with retry logic
      const [bandwidthRequests, agnRequests, webexRequests, iptRequests, circuitRequests] = await Promise.allSettled([
        this.getWithRetry('/api/requests', 3, 20000),
        this.getWithRetry('/api/agn-firewall', 3, 20000),
        this.getWithRetry('/api/webex-decommissioning', 3, 20000),
        this.getWithRetry('/api/ipt-decommissioning', 3, 20000),
        this.getWithRetry('/api/circuit-orders/', 3, 20000)
      ]);
      
      let allRequests = [];
      
      // Process results
      if (bandwidthRequests.status === 'fulfilled') {
        let bandwidthData = bandwidthRequests.value || [];

        // Apply user filtering
        if (!userRole.isAdmin && userRole.userEmail) {
          bandwidthData = bandwidthData.filter(req => req.customer_contact_mail === userRole.userEmail);
        }

        allRequests.push(...bandwidthData.map(req => ({
          ...req,
          id: `Bandwith_Upgrade_${req.id}`,
          original_id: req.id,
          source: 'requests',
          request_type: req.request_type || 'Bandwidth Upgrade/Downgrade',
          acai_request_id: req.acai_request_id || req.acaiRequestId
        })));
      }
      
      if (agnRequests.status === 'fulfilled') {
        let agnData = agnRequests.value || [];
        
        // Apply user filtering
        if (!userRole.isAdmin && userRole.userEmail) {
          agnData = agnData.filter(req => req.customer_contact_mail === userRole.userEmail);
        }
        
        allRequests.push(...agnData.map(req => ({
          ...req,
          id: `Firewall_Decom_${req.id}`,
          original_id: req.id,
          source: 'agn_firewall_requests',
          request_type: req.odm_type || 'AGN Managed-Firewall Decommissioning',
          odm_title: req.odm_title || req.odmTitle || 'N/A',
          request_state: req.request_state || req.requestState || 'N/A',
          request_status: req.request_status || req.requestStatus || 'N/A',
          approval_status: req.approval_status || req.approvalStatus || 'N/A',
          acai_request_id: req.acai_request_id || req.acaiRequestId
        })));
      }

      if (webexRequests.status === 'fulfilled') {
        let webexData = webexRequests.value || [];
        if (!userRole.isAdmin && userRole.userEmail) {
          webexData = webexData.filter(req => req.customer_contact_mail === userRole.userEmail);
        }

        allRequests.push(...webexData.map(req => ({
          ...req,
          id: `Webex_Decom_${req.id}`,
          original_id: req.id,
          source: 'webex_decommissioning_requests',
          request_type: req.odm_type || 'AGN Webex Decommissioning',
          odm_title: req.odm_title || req.odmTitle || 'N/A',
          request_state: req.request_state || req.requestState || 'N/A',
          request_status: req.request_status || req.requestStatus || 'N/A',
          approval_status: req.approval_status || req.approvalStatus || 'N/A',
          excel_file_name: req.excel_file_name || req.excelFileName,
          excel_file_size: req.excel_file_size || req.excelFileSize,
          has_excel_attachment: !!(req.excel_file_name || req.excelFileName),
          acai_request_id: req.acai_request_id || req.acaiRequestId
        })));
      }

      if (iptRequests.status === 'fulfilled') {
        // IPT endpoint returns { success: true, requests: [...] }, not a direct array
        let iptData = iptRequests.value?.requests || iptRequests.value || [];
        if (!userRole.isAdmin && userRole.userEmail) {
          iptData = iptData.filter(req => req.customer_contact_mail === userRole.userEmail);
        }

        allRequests.push(...iptData.map(req => ({
          ...req,
          id: `IPT_Decom_${req.id}`,
          original_id: req.id,
          source: 'ipt_decommissioning_requests',
          request_type: req.odm_type || 'AGN IPT Decommissioning',
          odm_title: req.odm_title || req.odmTitle || 'N/A',
          request_state: req.request_state || req.requestState || 'N/A',
          request_status: req.request_status || req.requestStatus || 'N/A',
          approval_status: req.approval_status || req.approvalStatus || 'N/A',
          excel_file_name: req.excel_file_name || req.excelFileName,
          excel_file_size: req.excel_file_size || req.excelFileSize,
          has_excel_attachment: !!(req.excel_file_name || req.excelFileName),
          acai_request_id: req.acai_request_id || req.acaiRequestId
        })));
      }

      if (circuitRequests.status === 'fulfilled') {
        let circuitData = circuitRequests.value || [];
        if (!userRole.isAdmin && userRole.userEmail) {
          circuitData = circuitData.filter(req => req.customer_contact_mail === userRole.userEmail);
        }
        allRequests.push(...circuitData.map(req => ({
          ...req,
          id: `Circuit_Order_${req.id}`,
          original_id: req.id,
          source: 'circuit_order_requests',
          request_type: 'Circuit Ordering',
          odm_title: req.odm_title || 'N/A',
          request_state: req.request_state || 'Draft',
          request_status: req.request_status || 'New',
          approval_status: req.approval_status || 'Pending',
          acai_request_id: req.acai_request_id,
        })));
      }

      // Update cache
      this.updateRequestsCache(
        bandwidthRequests.status === 'fulfilled' ? bandwidthRequests.value : [],
        agnRequests.status === 'fulfilled' ? agnRequests.value : [],
        webexRequests.status === 'fulfilled' ? webexRequests.value : [],
        iptRequests.status === 'fulfilled' ? (iptRequests.value?.requests || iptRequests.value || []) : [],
        circuitRequests.status === 'fulfilled' ? (circuitRequests.value || []) : []
      );
      
      return allRequests;
      
    } catch (error) {
      console.error('❌ Failed to fetch requests:', error);
      
      // Try to return stale cache as fallback
      const staleCache = this._requestsCache;
      if (staleCache) {
        console.log('📋 Using stale cache due to fetch failure');
        return staleCache;
      }
      
      throw error;
    }
  }

  // ============================================================================
  // WEBEX DECOMMISSIONING METHODS
  // ============================================================================

    static async postWebexDecommission(endpoint, data, options = {}) {
    const isFormData = data instanceof FormData;
    const authHeaders = await this.getDefaultHeaders();
    const { 'Content-Type': _ct, ...authWithoutContentType } = authHeaders;
    const fetchOptions = {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      ...options
    };
    if (!isFormData) {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers
      };
    } else {
      fetchOptions.headers = {
        ...authWithoutContentType,
        ...(options.headers || {})
      };
    }

    const response = await fetch(`${config.apiUrl}${endpoint}`, fetchOptions);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }
    return await response.json();
  }

  static async putWebexDecommission(endpoint, data, options = {}) {
    const isFormData = data instanceof FormData;
    const authHeaders = await this.getDefaultHeaders();
    const { 'Content-Type': _ct, ...authWithoutContentType } = authHeaders;

    const fetchOptions = {
      method: 'PUT',
      body: isFormData ? data : JSON.stringify(data),
      ...options
    };
    if (!isFormData) {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers
      };
    } else {
      fetchOptions.headers = {
        ...authWithoutContentType,
        ...(options.headers || {})
      };
    }

    const response = await fetch(`${config.apiUrl}${endpoint}`, fetchOptions);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }
    return await response.json();
  }


  static async downloadWebexExcel(requestId) {
    try {
      const response = await fetch(`${config.apiUrl}/api/webex-decommissioning/${requestId}/download-excel`);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error(`❌ Error downloading Excel file from Webex request ${requestId}:`, error);
      throw error;
    }
  }


  static async downloadWebexExcelFromAmelia(requestId) {
    try {
      const response = await fetch(`${config.apiUrl}/api/webex-decommissioning/${requestId}/download-excel-from-amelia`);
    if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error(`❌ Error downloading Excel file from Amelia for request ${requestId}:`, error);
      throw error;
    }
  }

  // IPT Decommissioning Methods
  static async postIPTDecommission(endpoint, data, options = {}) {
    const isFormData = data instanceof FormData;
    const authHeaders = await this.getDefaultHeaders();
    const { 'Content-Type': _ct, ...authWithoutContentType } = authHeaders;
    const fetchOptions = {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      ...options
    };
    if (!isFormData) {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers
      };
    } else {
      fetchOptions.headers = {
        ...authWithoutContentType,
        ...(options.headers || {})
      };
    }

    const response = await fetch(`${config.apiUrl}${endpoint}`, fetchOptions);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }
    return await response.json();
  }

  static async putIPTDecommission(endpoint, data, options = {}) {
    const isFormData = data instanceof FormData;
    const authHeaders = await this.getDefaultHeaders();
    const { 'Content-Type': _ct, ...authWithoutContentType } = authHeaders;

    const fetchOptions = {
      method: 'PUT',
      body: isFormData ? data : JSON.stringify(data),
      ...options
    };
    if (!isFormData) {
      fetchOptions.headers = {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers
      };
    } else {
      fetchOptions.headers = {
        ...authWithoutContentType,
        ...(options.headers || {})
      };
    }

    const response = await fetch(`${config.apiUrl}${endpoint}`, fetchOptions);
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`API Error: ${response.status} - ${errorText}`);
    }
    return await response.json();
  }

  static async downloadIPTExcel(requestId) {
    try {
      const response = await fetch(`${config.apiUrl}/api/ipt-decommissioning/${requestId}/download-excel`);

      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }

      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error(`❌ Error downloading Excel file from IPT request ${requestId}:`, error);
      throw error;
    }
  }

  // ============================================================================
  // CIRCUIT ORDERING METHODS
  // ============================================================================

  static async createCircuitOrder(data) {
    const response = await fetch(`${config.apiUrl}/api/circuit-orders/`, {
      method: 'POST',
      headers: await this.getDefaultHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.text();
      throw new Error(`API Error: ${response.status} - ${err}`);
    }
    return response.json();
  }

  static async submitCircuitOrder(requestId, costEstimate) {
    const response = await fetch(`${config.apiUrl}/api/circuit-orders/${requestId}/submit`, {
      method: 'POST',
      headers: await this.getDefaultHeaders(),
      body: JSON.stringify({ tem_cost_estimate: costEstimate || null }),
    });
    if (!response.ok) {
      const err = await response.text();
      throw new Error(`API Error: ${response.status} - ${err}`);
    }
    return response.json();
  }

  static async updateCircuitOrder(requestId, data) {
    const response = await fetch(`${config.apiUrl}/api/circuit-orders/${requestId}`, {
      method: 'PUT',
      headers: await this.getDefaultHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const err = await response.text();
      throw new Error(`API Error: ${response.status} - ${err}`);
    }
    return response.json();
  }

  static async getCircuitOrder(requestId) {
    return this.get(`/api/circuit-orders/${requestId}`);
  }

  static async getCircuitOrderComparison(requestId) {
    return this.get(`/api/circuit-orders/${requestId}/quote-comparison`);
  }

  static async selectCircuitOrderVendor(requestId, selectedVendor, comment) {
    return this.post(`/api/circuit-orders/${requestId}/select-vendor`, {
      selected_vendor: selectedVendor,
      gitp_selection_comment: comment,
    });
  }

  static async approveCircuitOrderQuote(requestId, status) {
    return this.post(`/api/circuit-orders/${requestId}/oe-approval`, { decision: status });
  }

  static async temSiteIds(country, city) {
    const params = new URLSearchParams();
    if (country) params.set('country', country);
    if (city) params.set('city', city);
    const qs = params.toString();
    return this.get(`/api/circuit-orders/tem-site-ids${qs ? `?${qs}` : ''}`);
  }

  static async circuitCostEstimate(siteId, accessSpeedMbps, country) {
    let url = `/api/circuit-orders/cost-estimate?site_id=${encodeURIComponent(siteId)}&access_speed_mbps=${accessSpeedMbps}`;
    if (country) url += `&country=${encodeURIComponent(country)}`;
    return this.get(url);
  }

  static async temLookup(siteId) {
    return this.get(`/api/circuit-orders/tem-lookup/${encodeURIComponent(siteId)}`);
  }

  static async temCountriesCities() {
    return this.get('/api/circuit-orders/tem-countries-cities');
  }

  static async generateSiteId(country, city) {
    return this.get(`/api/circuit-orders/generate-site-id?country=${encodeURIComponent(country)}&city=${encodeURIComponent(city)}`);
  }

  static async downloadCircuitOrderQuoteExcel(requestId) {
    const response = await fetch(`${config.apiUrl}/api/circuit-orders/${requestId}/generate-quote-excel`);
    if (!response.ok) throw new Error(`API Error: ${response.status}`);
    return response.blob();
  }

  // Update cache duration to 5 minutes:
  static CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
}

// Add this new method to the ApiService class


export default ApiService;